import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService, type User, type LoginRequest, type LoginResponse } from '../services/auth';

interface AuthState {
  // 状态
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  permissions: string[];
  roles: string[];

  // 操作
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  getProfile: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  checkPermission: (permission: string) => boolean;
  checkPermissions: (permissions: string[]) => boolean;
  clearAuth: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      // 初始状态
      isAuthenticated: false,
      isLoading: false,
      user: null,
      token: null,
      permissions: [],
      roles: [],

      // 登录
      login: async (credentials: LoginRequest) => {
        try {
          set({ isLoading: true });
          
          const response = await authService.login(credentials);
          const { token, user, permissions, roles } = response.data;

          set({
            isAuthenticated: true,
            user,
            token,
            permissions,
            roles,
            isLoading: false,
          });

          return true;
        } catch (error) {
          set({ isLoading: false });
          console.error('登录失败:', error);
          return false;
        }
      },

      // 退出登录
      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {
          console.error('退出登录失败:', error);
        } finally {
          get().clearAuth();
        }
      },

      // 获取用户资料
      getProfile: async () => {
        try {
          set({ isLoading: true });
          
          const response = await authService.getProfile();
          const user = response.data;

          set({
            user,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          console.error('获取用户资料失败:', error);
          // 如果获取用户资料失败，可能是token过期，清除认证状态
          get().clearAuth();
        }
      },

      // 更新用户资料
      updateProfile: async (data: Partial<User>) => {
        try {
          set({ isLoading: true });
          
          await authService.updateProfile(data);
          
          // 更新本地用户信息
          const currentUser = get().user;
          if (currentUser) {
            set({
              user: { ...currentUser, ...data },
              isLoading: false,
            });
          }
        } catch (error) {
          set({ isLoading: false });
          console.error('更新用户资料失败:', error);
          throw error;
        }
      },

      // 检查单个权限
      checkPermission: (permission: string) => {
        const { permissions } = get();
        return permissions.includes(permission) || permissions.includes('*:*');
      },

      // 检查多个权限（需要全部满足）
      checkPermissions: (permissions: string[]) => {
        const { permissions: userPermissions } = get();
        
        // 系统管理员拥有所有权限
        if (userPermissions.includes('*:*')) {
          return true;
        }
        
        return permissions.every(permission => userPermissions.includes(permission));
      },

      // 清除认证状态
      clearAuth: () => {
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          permissions: [],
          roles: [],
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
        permissions: state.permissions,
        roles: state.roles,
      }),
    }
  )
);